<div class="push-notifications-container p-8 max-w-7xl mx-auto">
  <h1 class="mb-8 text-3xl font-medium">{{ 'push-notifications.title' | translate }}</h1>

  <form [formGroup]="form" (ngSubmit)="sendNotification()">
    <!-- Stage 1: Target Audience Selection -->
    @if (currentStage() === 1) {
      <mat-card>
        <mat-card-content>
          <div class="mb-4">
            <h2 class="text-2xl font-medium mb-2">{{ 'push-notifications.stage1.title' | translate }}</h2>
            <p class="text-sm text-gray-600 mb-4">{{ 'push-notifications.stage1.description' | translate }}</p>
          </div>

          <div formArrayName="targets">
            @for (target of form.controls.targets.controls; track $index; let idx = $index) {
              <mat-card class="mb-4 border-l-4 border-indigo-600">
                <mat-card-content [formGroupName]="idx">
                  <div class="flex justify-between items-center mb-4">
                    <h3 class="m-0 text-lg font-medium">{{ 'push-notifications.form.target-label' | translate }} {{ idx + 1 }}</h3>
                    @if (form.controls.targets.length > 1) {
                      <button mat-icon-button (click)="removeTarget(idx)" type="button"
                              [matTooltip]="'push-notifications.form.remove-target' | translate">
                        <mat-icon>delete</mat-icon>
                      </button>
                    }
                  </div>

                  <!-- Target Type Selection -->
                  <mat-form-field appearance="outline" class="w-full mb-4">
                    <mat-label>{{ 'push-notifications.form.target-type-label' | translate }}</mat-label>
                    <mat-select formControlName="type">
                      <mat-option [value]="TargetType.Global">{{ getTargetTypeLabel(TargetType.Global) }}</mat-option>
                      <mat-option [value]="TargetType.Reservation">{{ getTargetTypeLabel(TargetType.Reservation) }}</mat-option>
                      <mat-option [value]="TargetType.Water">{{ getTargetTypeLabel(TargetType.Water) }}</mat-option>
                      <mat-option [value]="TargetType.ArrivalDate">{{ getTargetTypeLabel(TargetType.ArrivalDate) }}</mat-option>
                      <mat-option [value]="TargetType.User">{{ getTargetTypeLabel(TargetType.User) }}</mat-option>
                    </mat-select>
                  </mat-form-field>

                  <!-- Conditional Fields Based on Target Type -->
                  @if (target.controls.type.value === TargetType.Reservation) {
                    <mat-form-field appearance="outline" class="w-full">
                      <mat-label>{{ 'push-notifications.form.reservation-number-label' | translate }}</mat-label>
                      <input matInput formControlName="reservationNumber"
                             [placeholder]="'push-notifications.form.reservation-number-placeholder' | translate"/>
                    </mat-form-field>
                  }
                  @if (target.controls.type.value === TargetType.Water) {
                    <div class="mb-4">
                      @if (target.controls.waterId.value) {
                        <div class="flex items-center justify-between p-2 px-4 bg-gray-100 rounded">
                          <span class="flex-1">{{ target.controls.waterTitle.value }}</span>
                          <button mat-icon-button (click)="clearWater(idx)" type="button"
                                  [matTooltip]="'push-notifications.form.clear-water' | translate">
                            <mat-icon>close</mat-icon>
                          </button>
                        </div>
                      } @else {
                        <button mat-raised-button (click)="selectWater(idx)" type="button">
                          <mat-icon>search</mat-icon>
                          {{ 'push-notifications.form.select-water' | translate }}
                        </button>
                      }
                    </div>
                  }
                  @if (target.controls.type.value === TargetType.ArrivalDate) {
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <mat-form-field appearance="outline">
                        <mat-label>{{ 'push-notifications.form.date-from-label' | translate }}</mat-label>
                        <input matInput [matDatepicker]="pickerFrom" formControlName="arrivalDateFrom"/>
                        <mat-datepicker-toggle matIconSuffix [for]="pickerFrom"/>
                        <mat-datepicker #pickerFrom/>
                      </mat-form-field>

                      <mat-form-field appearance="outline">
                        <mat-label>{{ 'push-notifications.form.date-to-label' | translate }}</mat-label>
                        <input matInput [matDatepicker]="pickerTo" formControlName="arrivalDateTo"/>
                        <mat-datepicker-toggle matIconSuffix [for]="pickerTo"/>
                        <mat-datepicker #pickerTo/>
                        <mat-hint>{{ 'push-notifications.form.date-to-hint' | translate }}</mat-hint>
                      </mat-form-field>
                    </div>
                  }
                  @if (target.controls.type.value === TargetType.User) {
                    <mat-form-field appearance="outline" class="w-full">
                      <mat-label>{{ 'push-notifications.form.user-email-label' | translate }}</mat-label>
                      <input matInput type="email" formControlName="userEmail"
                             [placeholder]="'push-notifications.form.user-email-placeholder' | translate"/>
                    </mat-form-field>
                  }
                </mat-card-content>
              </mat-card>
            }
          </div>

          <button mat-raised-button (click)="addTarget()" type="button" class="mt-4">
            <mat-icon>add</mat-icon>
            {{ 'push-notifications.form.add-target' | translate }}
          </button>

          <!-- Continue Button -->
          <div class="mt-8 flex justify-end">
            <button mat-raised-button color="primary" type="button" (click)="proceedToStage2()" [disabled]="loadingUserCounts()">
              @if (loadingUserCounts()) {
                <mat-spinner diameter="20"/>
              } @else {
                <mat-icon>arrow_forward</mat-icon>
              }
              {{ 'push-notifications.stage1.continue' | translate }}
            </button>
          </div>
        </mat-card-content>
      </mat-card>
    }

    <!-- Stage 2: Language Selection & Content -->
    @if (currentStage() === 2) {
      <mat-card>
        <mat-card-content>
          <div class="mb-4">
            <h2 class="text-2xl font-medium mb-2">{{ 'push-notifications.stage2.title' | translate }}</h2>
            <p class="text-sm text-gray-600 mb-4">{{ 'push-notifications.stage2.description' | translate }}</p>
          </div>

          <!-- Reservation site distribution message (informational only, shown for arrival-date targets).
               The per-site numbers are shown on each language card below. -->
          @if (hasSiteInfo()) {
            <mat-card class="mb-6 bg-blue-50 dark:bg-blue-950/40 border-l-4 border-blue-500 dark:border-blue-400">
              <mat-card-content>
                <div class="flex items-start gap-3">
                  <mat-icon class="!text-blue-600 dark:!text-blue-300">info</mat-icon>
                  <div>
                    <h3 class="text-base font-medium m-0 mb-1 text-gray-900 dark:text-gray-100">{{ 'push-notifications.stage2.site-distribution-title' | translate }}</h3>
                    <p class="text-sm text-gray-600 dark:text-gray-400 m-0">{{ 'push-notifications.stage2.site-distribution-hint' | translate }}</p>
                  </div>
                </div>
              </mat-card-content>
            </mat-card>
          }

          <!-- Language Selection with Toggles -->
          <div class="space-y-4 mb-6">
            @for (locale of locales; track locale.code) {
              <mat-card class="border" [class.border-indigo-600]="locale.enabled" [class.border-gray-300]="!locale.enabled">
                <mat-card-content>
                  @let siteChips = getSiteChips(locale.code);
                  <!-- Language Header with Toggle -->
                  <div class="flex items-center justify-between mb-2">
                    <div class="flex items-center gap-4">
                      <button
                        mat-icon-button
                        type="button"
                        (click)="toggleLanguage(locale.code)"
                        [color]="locale.enabled ? 'primary' : 'default'">
                        <mat-icon>{{ locale.enabled ? 'check_box' : 'check_box_outline_blank' }}</mat-icon>
                      </button>
                      <h3 class="text-lg font-medium m-0">{{ locale.label }}</h3>
                      <span class="text-sm text-gray-600 dark:text-gray-400">
                        ({{ getUserCount(locale.code) }} {{ 'push-notifications.stage2.in-app' | translate }},
                        {{ getPushTokenCount(locale.code) }} {{ 'push-notifications.stage2.push' | translate }})
                      </span>
                    </div>
                    @if (siteChips.length > 0) {
                      <div class="flex items-center gap-1.5 flex-wrap">
                        <span class="text-xs text-gray-500 dark:text-gray-400">{{ 'push-notifications.stage2.sites-label' | translate }}</span>
                        @for (site of siteChips; track site.site) {
                          <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white dark:bg-gray-800 border border-blue-200 dark:border-blue-800 text-xs">
                            <span class="font-medium uppercase text-gray-900 dark:text-gray-100">{{ site.site }}</span>
                            <span class="text-gray-600 dark:text-gray-400">{{ site.count }}</span>
                          </span>
                        }
                      </div>
                    }
                  </div>

                  <!-- Language Content (Title & Body) - Only shown when enabled -->
                  @if (locale.enabled) {
                    <div class="mt-4 space-y-4 pl-12">
                      <!-- Title -->
                      <mat-form-field appearance="outline" class="w-full">
                        <mat-label>{{ 'push-notifications.form.title-label' | translate }}</mat-label>
                        <input
                          matInput
                          [formControlName]="getTitleControlName(locale.code)"
                          [placeholder]="'push-notifications.form.title-placeholder' | translate"
                          maxlength="100" />
                        @if (form.get(getTitleControlName(locale.code))?.hasError('required')) {
                          <mat-error>{{ 'push-notifications.form.title-required' | translate }}</mat-error>
                        }
                        @if (form.get(getTitleControlName(locale.code))?.hasError('maxlength')) {
                          <mat-error>{{ 'push-notifications.form.title-too-long' | translate }}</mat-error>
                        }
                        <mat-hint align="end">{{ form.get(getTitleControlName(locale.code))?.value.length || 0 }}/100</mat-hint>
                      </mat-form-field>

                      <!-- Body -->
                      <mat-form-field appearance="outline" class="w-full">
                        <mat-label>{{ 'push-notifications.form.body-label' | translate }}</mat-label>
                        <textarea
                          matInput
                          [formControlName]="getBodyControlName(locale.code)"
                          [placeholder]="'push-notifications.form.body-placeholder' | translate"
                          rows="4"
                          maxlength="500"
                        ></textarea>
                        @if (form.get(getBodyControlName(locale.code))?.hasError('required')) {
                          <mat-error>{{ 'push-notifications.form.body-required' | translate }}</mat-error>
                        }
                        @if (form.get(getBodyControlName(locale.code))?.hasError('maxlength')) {
                          <mat-error>{{ 'push-notifications.form.body-too-long' | translate }}</mat-error>
                        }
                        <mat-hint align="end">{{ form.get(getBodyControlName(locale.code))?.value.length || 0 }}/500</mat-hint>
                      </mat-form-field>
                    </div>
                  }
                </mat-card-content>
              </mat-card>
            }
          </div>

          <!-- AI Translation Section -->
          <mat-card class="mb-6 bg-purple-50">
            <mat-card-content>
              <div class="flex items-center gap-4 flex-wrap">
                <h3 class="text-lg font-medium">{{ 'push-notifications.form.translate-title' | translate }}</h3>

                <button
                  mat-raised-button
                  color="accent"
                  (click)="translateMissing()"
                  [disabled]="translating()"
                  type="button"
                  class="ml-auto">
                  @if (translating()) {
                    <mat-spinner diameter="20" />
                  } @else {
                    <mat-icon>translate</mat-icon>
                  }
                  {{ 'push-notifications.form.translate-missing-button' | translate }}
                </button>
              </div>
              <p class="text-sm text-gray-600 mt-2">{{ 'push-notifications.form.translate-missing-hint' | translate }}</p>
            </mat-card-content>
          </mat-card>

          <!-- Result Display -->
          @if (lastResult()) {
            <mat-card class="mb-6 border-l-4" [class.bg-green-50]="lastResult()?.success"
                      [class.border-green-500]="lastResult()?.success" [class.bg-red-50]="!lastResult()?.success"
                      [class.border-red-500]="!lastResult()?.success">
              <mat-card-content>
                <div class="flex items-center gap-4">
                  <mat-icon class="!text-3xl !w-8 !h-8"
                            [class]="lastResult()?.success ? '!text-green-500' : '!text-red-500'">{{ lastResult()?.success ? 'check_circle' : 'error' }}
                  </mat-icon>
                  <p class="m-0 text-base">{{ lastResult()?.message }}</p>
                </div>
              </mat-card-content>
            </mat-card>
          }

          <!-- Actions -->
          <div class="mt-8 flex justify-between">
            <button mat-raised-button type="button" (click)="backToStage1()">
              <mat-icon>arrow_back</mat-icon>
              {{ 'push-notifications.stage2.back' | translate }}
            </button>

            <button mat-raised-button color="primary" type="submit" [disabled]="sending() || form.invalid">
              {{ (sending() ? 'push-notifications.form.sending' : 'push-notifications.form.send') | translate }}
              @if (sending()) {
                <mat-spinner diameter="20"/>
              } @else {
                <mat-icon>send</mat-icon>
              }
            </button>
          </div>
        </mat-card-content>
      </mat-card>
    }
  </form>
</div>
